import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { ApprovalBadge } from "@/components/app/ClientApprovalPanel";
import { formatINR, formatDate, statusTone } from "@/lib/format";
import ProjectExtraFields from "@/components/app/ProjectExtraFields";
import { StatusBadge, statusPalette } from "@/lib/statusColor";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Settings2, Plus, Pencil, Check, X, Upload, Download, FileSpreadsheet } from "lucide-react";

const PSTATUSES_FALLBACK = ["Planned", "Active", "In Progress", "On Hold", "Completed"];

export default function Projects() {
  const { user } = useAuth();
  const isSuperAdmin = user.role === "SuperAdmin";
  const isSaOrLead = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const isManager = ["Manager", "TeamLeader", "SuperAdmin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [managers, setManagers] = useState([]);
  const [params, setParams] = useSearchParams();
  const [newOpen, setNewOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ name: "", client_name: "", manager_id: "", status: "Planned", budget: 0, start_date: "", due_date: "", priority: "Medium", assigned_freelancer_ids: [], backup_manager_id: "", service_id: "", vendor_id: "", sme_id: "", service_name: "", writer: "", reviewer: "", biostatistician: "", uploader: "", journals: [{ name: "", url: "" }, { name: "", url: "" }, { name: "", url: "" }] });
  const [services, setServices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const savedFilters = (() => { try { return JSON.parse(localStorage.getItem("fms_projects_filters") || "{}"); } catch { return {}; } })();
  const [smes, setSmes] = useState([]);
  const [smeFilter, setSmeFilter] = useState(savedFilters.smeFilter || "");
  const [view, setView] = useState(savedFilters.view || "table");
  const [editing, setEditing] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [smeList, setSmeList] = useState([]);
  useEffect(() => {
    api.get("/vendors").then((r) => setVendorList(r.data)).catch(() => {});
    api.get("/smes").then((r) => setSmeList(r.data)).catch(() => {});
  }, []);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [renameFor, setRenameFor] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [search, setSearch] = useState(savedFilters.search || "");
  const [statusFilters, setStatusFilters] = useState(Array.isArray(savedFilters.statusFilters) ? savedFilters.statusFilters : []);
  const [statusDropOpen, setStatusDropOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState(savedFilters.clientFilter || "");
  const [dueBefore, setDueBefore] = useState(savedFilters.dueBefore || "");
  const [managerFilter, setManagerFilter] = useState(savedFilters.managerFilter || "");
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || "newest");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/projects/import-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "frelanctrac-projects-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitImport = async () => {
    if (!importFile) { toast.error("Choose a CSV file first"); return; }
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/projects/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (data.created_count > 0) toast.success(`Imported ${data.created_count} project${data.created_count > 1 ? "s" : ""}`);
      if (data.failed_count > 0) toast.error(`${data.failed_count} row${data.failed_count > 1 ? "s" : ""} failed — see details`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setImportBusy(false);
  };

  const load = async () => {
    const { data } = await api.get("/projects"); setItems(data);
    if (isManager) {
      const [f, s, c, sv, vd, sm] = await Promise.all([
        api.get("/freelancers"),
        api.get("/project-statuses").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/services").catch(() => ({ data: [] })),
        api.get("/vendors").catch(() => ({ data: [] })),
        api.get("/smes").catch(() => ({ data: [] })),
      ]);
      setFreelancers(f.data); setStatuses(s.data); setClients(c.data); setServices(sv.data);
      setVendors(vd.data); setSmes(sm.data);
    }
    if (isSaOrLead) {
      try {
        const u = await api.get("/users");
        setManagers(u.data.filter((x) => ["Manager", "TeamLeader", "SuperAdmin"].includes(x.role) && x.status !== "deactivated"));
      } catch { setManagers([]); }
    } else if (user.role === "Manager") {
      // Manager can only assign to themselves
      setManagers([{ id: user.id, name: user.name, email: user.email, role: "Manager" }]);
    }
  };
  useEffect(() => { load(); }, []);

  // Persist every filter choice so navigating into a project and back keeps them intact
  useEffect(() => {
    localStorage.setItem("fms_projects_filters", JSON.stringify({ search, statusFilters, clientFilter, managerFilter, dueBefore, smeFilter, sortBy, view }));
  }, [search, statusFilters, clientFilter, managerFilter, dueBefore, smeFilter, sortBy, view]);

  useEffect(() => {
    if (params.get("client_name")) {
      setForm((f) => ({ ...f, client_name: params.get("client_name") }));
      setNewOpen(true);
    }
  }, [params]);

  // Managers auto-assigned to themselves; SuperAdmins pick from dropdown
  useEffect(() => {
    if (!isSaOrLead && user?.role === "Manager" && !form.manager_id) {
      setForm((f) => ({ ...f, manager_id: user.id }));
    }
  }, [user, isSaOrLead]);

  const reassignManager = async (p, mgrId) => {
    try {
      await api.patch(`/projects/${p.id}`, { manager_id: mgrId });
      toast.success("Manager reassigned");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Bulk selection ----
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/projects/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted}${data.skipped ? ` · ${data.skipped} skipped (no access)` : ""}`);
      setBulkConfirmDelete(false); clearSelection(); load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBulkBusy(false);
  };
  const bulkChangeStatus = async () => {
    if (!bulkStatus) { toast.error("Pick a status"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/projects/bulk-status", { ids: Array.from(selected), status: bulkStatus });
      toast.success(`Updated ${data.updated}${data.skipped ? ` · ${data.skipped} skipped (no access)` : ""}`);
      setBulkStatus(""); clearSelection(); load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBulkBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (!form.manager_id) { toast.error("Assign a manager to the project"); return; }
      await api.post("/projects", { ...form, budget: Number(form.budget), backup_manager_id: form.backup_manager_id || null, service_id: form.service_id || null });
      setNewOpen(false); setParams({});
      setForm({ name: "", client_name: "", manager_id: isSaOrLead ? "" : user.id, status: "Planned", budget: 0, start_date: "", due_date: "", priority: "Medium", assigned_freelancer_ids: [], backup_manager_id: "", service_id: "" });
      load(); toast.success("Project created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateStatus = async (p, status) => {
    try { await api.patch(`/projects/${p.id}`, { status }); load(); toast.success("Status updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? This can't be undone.`)) return;
    try { await api.delete(`/projects/${p.id}`); load(); toast.success("Project deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, budget: Number(editing.budget) };
      delete payload.id; delete payload.created_at; delete payload.files; delete payload.updated_at;
      await api.patch(`/projects/${editing.id}`, payload);
      setEditing(null); load(); toast.success("Project updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const statusOptions = statuses.length > 0 ? statuses.map((s) => s.name) : PSTATUSES_FALLBACK;

  const addStatus = async () => {
    const name = newStatus.trim();
    if (!name) return;
    try { await api.post("/project-statuses", { name }); setNewStatus(""); await load(); toast.success("Status added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const saveRename = async (s) => {
    const name = renameValue.trim();
    if (!name || name === s.name) { setRenameFor(null); return; }
    try { await api.patch(`/project-statuses/${s.id}`, { name }); await load(); setRenameFor(null); toast.success("Renamed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const removeStatus = async (s) => {
    if (!window.confirm(`Delete status "${s.name}"? Projects using it must be moved first.`)) return;
    try { await api.delete(`/project-statuses/${s.id}`); await load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const filtered = items.filter((p) => {
    if (search && ![p.name, p.client_name, p.project_number].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))) return false;
    if (statusFilters.length && !statusFilters.includes(p.status)) return false;
    if (clientFilter && p.client_name !== clientFilter) return false;
    if (managerFilter && p.manager_id !== managerFilter) return false;
    if (dueBefore && p.due_date && p.due_date > dueBefore) return false;
    if (smeFilter && p.sme_id !== smeFilter) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === "updated") {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    }
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return sortBy === "oldest" ? ta - tb : tb - ta;
  });

  const statusColor = (name) => {
    const row = statuses.find((s) => s.name === name);
    return row?.color || "#F5F5F5";
  };

  return (
    <div className="projects-module">
      <PageHeader
        title="Projects"
        description={isManager ? "All active projects across clients." : "Projects you're assigned to."}
        actions={isManager && (
          <>
            <button data-testid="manage-statuses-btn" onClick={() => setStatusesOpen(true)} className="border-2 border-black bg-white px-3 py-2 text-sm hard-shadow-hover inline-flex items-center gap-2"><Settings2 size={14} /> Manage statuses</button>
            <div className="border border-black inline-flex" role="tablist">
              <button data-testid="projects-view-table" onClick={() => setView("table")} className={`px-3 py-2 text-xs font-mono-tight ${view === "table" ? "bg-black text-white" : "bg-white"}`}>Table</button>
              <button data-testid="projects-view-kanban" onClick={() => setView("kanban")} className={`px-3 py-2 text-xs font-mono-tight ${view === "kanban" ? "bg-black text-white" : "bg-white"}`}>Kanban</button>
            </div>
            <button data-testid="import-projects-btn" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} className="border-2 border-black bg-white px-3 py-2 text-sm hard-shadow-hover inline-flex items-center gap-2"><Upload size={14} /> Import Projects</button>
            <button data-testid="new-project-btn" onClick={() => setNewOpen(true)} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">+ New Project</button>
          </>
        )}
      />
      <div className="p-8">
        <div className="mb-4 flex flex-wrap gap-2 items-center" data-testid="projects-filters">
          <Input data-testid="projects-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search project or client…" className="h-9 rounded-lg border-black/15 max-w-xs" />
          <div className="relative" data-testid="projects-filter-status">
            <button type="button" data-testid="projects-filter-status-toggle" onClick={() => setStatusDropOpen((o) => !o)}
              className="h-9 border border-black/15 rounded-lg px-3 text-sm bg-white inline-flex items-center gap-2">
              {statusFilters.length === 0 ? "All statuses" : `${statusFilters.length} status${statusFilters.length > 1 ? "es" : ""} selected`}
              <span className="text-neutral-400 text-xs">▾</span>
            </button>
            {statusDropOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setStatusDropOpen(false)} />
                <div className="absolute left-0 z-40 mt-1 w-52 bg-white border border-black/15 rounded-lg shadow-lg p-2 space-y-1" data-testid="projects-filter-status-menu">
                  {statusOptions.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-cream-2 cursor-pointer">
                      <input type="checkbox" data-testid={`projects-filter-status-opt-${s}`}
                        checked={statusFilters.includes(s)}
                        onChange={(e) => setStatusFilters(e.target.checked ? [...statusFilters, s] : statusFilters.filter((x) => x !== s))} />
                      {s}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          <select data-testid="projects-filter-client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
            <option value="">All clients</option>
            {[...new Set(items.map((i) => i.client_name).filter(Boolean))].map((c) => <option key={c}>{c}</option>)}
          </select>
          {isSaOrLead && (
            <select data-testid="projects-filter-manager" value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
              <option value="">All managers</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}{m.role === "SuperAdmin" ? " (SA)" : m.role === "TeamLeader" ? " (OH)" : ""}</option>)}
            </select>
          )}
          <Input data-testid="projects-filter-due" type="date" value={dueBefore} onChange={(e) => setDueBefore(e.target.value)} className="h-9 rounded-lg border-black/15 w-40" title="Due before" />
          <select data-testid="projects-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="updated">Last Modified</option>
          </select>
          <select data-testid="projects-filter-sme" value={smeFilter} onChange={(e) => setSmeFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
            <option value="">All SMEs</option>
            {smes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {(search || statusFilters.length > 0 || clientFilter || managerFilter || dueBefore || smeFilter) && (
            <button data-testid="projects-filters-clear" onClick={() => { setSearch(""); setStatusFilters([]); setClientFilter(""); setManagerFilter(""); setDueBefore(""); setSmeFilter(""); }} className="text-xs underline underline-offset-4">Clear</button>
          )}
          <div className="ml-auto text-xs text-neutral-500 font-mono-tight">{filtered.length} / {items.length}</div>
        </div>
        {view === "table" ? (
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="fms-table w-full" data-testid="projects-table">
              <thead><tr>
                {isManager && <th className="w-8"><input data-testid="bulk-select-all" type="checkbox" checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))} onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((p) => p.id)) : new Set())} /></th>}
                <th>Project ID</th><th>Client</th><th>Service</th><th>Manager</th><th>Status</th><th>Priority</th><th>Budget</th><th>Due</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`project-row-${p.id}`} data-approval={p.client_approval_status || "none"}
                    className={p.client_approval_status === "Client Approved" ? "bg-emerald-50 border-l-4 border-emerald-500" : p.client_approval_status === "Changes Requested" ? "bg-orange-50/70 border-l-4 border-orange-400" : p.client_approval_status === "Awaiting Approval" ? "bg-amber-50/60 border-l-4 border-amber-400" : ""}>
                    {isManager && <td><input data-testid={`bulk-select-${p.id}`} type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} /></td>}
                    <td className="font-mono-tight text-sm font-semibold whitespace-nowrap" title={p.name} data-testid={`project-id-${p.id}`}>{p.project_number || <span className="font-sans font-normal text-neutral-600">{p.name}</span>}
                      {p.client_approval_status && <div className="mt-1"><ApprovalBadge status={p.client_approval_status} testid={`project-approval-${p.id}`} /></div>}</td>
                    <td className="text-sm text-neutral-600">{p.client_name}</td>
                    <td className="text-xs text-neutral-600" data-testid={`project-service-${p.id}`}>{p.service_name || services.find((s) => s.id === p.service_id)?.name || "—"}</td>
                    <td>
                      {isSaOrLead ? (
                        <select data-testid={`project-manager-${p.id}`} value={p.manager_id || ""} onChange={(e) => reassignManager(p, e.target.value)}
                          className="px-2 py-0.5 text-xs font-mono-tight rounded border border-black/15 bg-white max-w-[180px]">
                          {!p.manager_id && <option value="">— unassigned —</option>}
                          {managers.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} {m.role === "SuperAdmin" ? "(SA)" : m.role === "TeamLeader" ? "(TL)" : ""}</option>)}
                        </select>
                      ) : (
                        <span className="text-xs text-neutral-600 font-mono-tight">{p.manager_name || "—"}</span>
                      )}
                    </td>
                    <td>
                      {isManager ? (
                        <select data-testid={`project-status-${p.id}`} value={p.status} onChange={(e) => updateStatus(p, e.target.value)}
                          style={{ background: statusPalette(p.status).bg, color: statusPalette(p.status).fg }}
                          className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded-full border border-black/10`}>
                          {statusOptions.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      ) : <StatusBadge status={p.status} testid={`status-badge-${p.id}`} />}
                    </td>
                    <td className="font-mono-tight text-xs">{p.priority}</td>
                    <td className="font-mono-tight num">{formatINR(p.budget)}</td>
                    <td className="font-mono-tight text-neutral-500">{formatDate(p.due_date)}</td>
                    <td className="text-right whitespace-nowrap space-x-3">
                      <Link data-testid={`view-project-${p.id}`} to={`/projects/${p.id}`} className="text-sm underline underline-offset-4">Open</Link>
                      {isManager && <button data-testid={`edit-project-${p.id}`} onClick={() => setEditing({ ...p })} className="text-xs underline underline-offset-4">Edit</button>}
                      {isManager && <button data-testid={`delete-project-${p.id}`} onClick={() => remove(p)} className="text-xs underline underline-offset-4 text-[#B02A00]">Delete</button>}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={10} className="text-center text-neutral-500 py-8">No projects yet.</td></tr>}
                {items.length > 0 && filtered.length === 0 && <tr><td colSpan={10} className="text-center text-neutral-500 py-8">No matches for your filters.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {statusOptions.map((st) => (
              <div key={st} className="border border-neutral-300 bg-white">
                <div className="px-3 py-2 border-b-2 border-black label-caps flex justify-between"><span>{st}</span><span className="font-mono-tight text-neutral-500">{items.filter((p) => p.status === st).length}</span></div>
                <div className="p-2 space-y-2 min-h-[100px]">
                  {items.filter((p) => p.status === st).map((p) => (
                    <Link to={`/projects/${p.id}`} key={p.id} className={`block border p-2 hover:shadow-[4px_4px_0_0_#0A0A0A] transition-shadow ${p.client_approval_status === "Client Approved" ? "border-emerald-500 bg-emerald-50" : "border-neutral-300 bg-white"}`}>
                      <div className="text-sm font-medium font-mono-tight">{p.project_number || p.name}</div>
                      {p.client_approval_status && <ApprovalBadge status={p.client_approval_status} testid={`kanban-approval-${p.id}`} />}
                      <div className="text-xs text-neutral-500">{p.client_name}</div>
                      <div className="text-xs font-mono-tight mt-1">{formatINR(p.budget)}</div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none border-2 border-black max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Project</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><label className="label-caps block mb-1">Name</label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-black" data-testid="new-project-name" /></div>
            <div><label className="label-caps block mb-1">Client</label><Input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="rounded-none border-black" data-testid="new-project-client" /></div>
            <div><label className="label-caps block mb-1">Assigned Manager *</label>
              <select required data-testid="new-project-manager" value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} disabled={!isSaOrLead} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white disabled:opacity-70">
                <option value="">— pick a manager —</option>
                {managers.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} {m.role === "SuperAdmin" ? "(SA)" : ""}</option>)}
              </select>
              {!isSaOrLead && <div className="text-[10px] text-neutral-500 font-mono-tight mt-1">Managers can only assign projects to themselves. Ask a Super Admin or Operation Head to reassign.</div>}
            </div>
            {isSaOrLead && (
              <div><label className="label-caps block mb-1">Backup Manager (auto-covers leave)</label>
                <select data-testid="new-project-backup" value={form.backup_manager_id} onChange={(e) => setForm({ ...form, backup_manager_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                  <option value="">— none (skipped on leave) —</option>
                  {managers.filter((m) => m.id !== form.manager_id).map((m) => <option key={m.id} value={m.id}>{m.name || m.email}{m.role === "SuperAdmin" ? " (SA)" : m.role === "TeamLeader" ? " (TL)" : ""}</option>)}
                </select>
              </div>
            )}
            <div><label className="label-caps block mb-1">Service Template (auto-populates phases)</label>
              <select data-testid="new-project-service" value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">— no template (phases can be added later) —</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {(s.phases || []).length} phases · {(s.phases || []).reduce((a, p) => a + (p.days || 0), 0)}d</option>)}
              </select>
              {form.service_id && (
                <div className="mt-2 text-[11px] text-neutral-500 font-mono-tight">
                  Preview: {(services.find((s) => s.id === form.service_id)?.phases || []).map((p, i) => `${i + 1}. ${p.name} (${p.days}d)`).join(" → ")}
                </div>
              )}
            </div>
            <div><label className="label-caps block mb-1">Project Type (Vendor)</label>
              <select data-testid="new-project-vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">Non-Type</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.company_name}</option>)}
              </select>
            </div>
            <div><label className="label-caps block mb-1">SME</label>
              <select data-testid="new-project-sme" value={form.sme_id} onChange={(e) => setForm({ ...form, sme_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">— no SME —</option>
                {smes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                  {statusOptions.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="label-caps block mb-1">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                  <option>Low</option><option>Medium</option><option>High</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Start</label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">Due</label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-none border-black" /></div>
            </div>
            <div><label className="label-caps block mb-1">Budget (INR)</label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">Assign Freelancers</label>
              <div className="border border-black p-2 max-h-40 overflow-auto space-y-1">
                {freelancers.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.assigned_freelancer_ids.includes(f.id)} onChange={(e) => {
                      const ids = e.target.checked ? [...form.assigned_freelancer_ids, f.id] : form.assigned_freelancer_ids.filter((x) => x !== f.id);
                      setForm({ ...form, assigned_freelancer_ids: ids });
                    }} />
                    {f.name} <span className="text-xs text-neutral-500">· {f.category}</span>
                  </label>
                ))}
              </div>
            </div>
            <ProjectExtraFields value={form} onChange={(v) => setForm({ ...form, ...v })} idPrefix="new-project" freelancers={freelancers} />
            <DialogFooter><button type="submit" data-testid="submit-project" className="bg-black text-white px-4 py-2 text-sm">Create</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-[1.25rem] max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit Project</DialogTitle></DialogHeader>
              <form onSubmit={submitEdit} className="space-y-3">
                <div><label className="label-caps block mb-1">Name</label><Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div><label className="label-caps block mb-1">Client</label><Input required value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} className="rounded-lg border-black/15" /></div>
                {isSuperAdmin && (
                  <div><label className="label-caps block mb-1">Assigned Manager</label>
                    <select data-testid="edit-project-manager" value={editing.manager_id || ""} onChange={(e) => setEditing({ ...editing, manager_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option value="">— pick a manager —</option>
                      {managers.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} {m.role === "SuperAdmin" ? "(SA)" : ""}</option>)}
                    </select>
                  </div>
                )}
                {isSaOrLead && (
                  <div><label className="label-caps block mb-1">Backup Manager</label>
                    <select data-testid="edit-project-backup" value={editing.backup_manager_id || ""} onChange={(e) => setEditing({ ...editing, backup_manager_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option value="">— none —</option>
                      {managers.filter((m) => m.id !== editing.manager_id).map((m) => <option key={m.id} value={m.id}>{m.name || m.email}{m.role === "SuperAdmin" ? " (SA)" : m.role === "TeamLeader" ? " (TL)" : ""}</option>)}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Status</label>
                    <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      {statusOptions.map((s) => <option key={s}>{s}</option>)}
                    </select></div>
                  <div><label className="label-caps block mb-1">Priority</label>
                    <select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option>Low</option><option>Medium</option><option>High</option>
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Start</label><Input type="date" value={editing.start_date || ""} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className="rounded-lg border-black/15" /></div>
                  <div><label className="label-caps block mb-1">Due</label><Input type="date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="rounded-lg border-black/15" /></div>
                </div>
                <div><label className="label-caps block mb-1">Budget (INR)</label><Input type="number" value={editing.budget} onChange={(e) => setEditing({ ...editing, budget: e.target.value })} className="rounded-lg border-black/15" /></div>
                <ProjectExtraFields value={editing} onChange={(v) => setEditing({ ...editing, ...v })} idPrefix="edit-project" freelancers={freelancers} serviceLocked={user.role === "Manager"} />
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Project Type (Vendor)</label>
                    <select data-testid="edit-project-vendor" value={editing.vendor_id || ""} onChange={(e) => setEditing({ ...editing, vendor_id: e.target.value })} disabled={!isSaOrLead}
                      title={isSaOrLead ? "" : "Only Super Admins and Operation Heads can change the project type"}
                      className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">Non-Type</option>
                      {vendorList.map((v) => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                    </select></div>
                  <div><label className="label-caps block mb-1">SME</label>
                    <select data-testid="edit-project-sme" value={editing.sme_id || ""} onChange={(e) => setEditing({ ...editing, sme_id: e.target.value })} disabled={!isSaOrLead}
                      title={isSaOrLead ? "" : "Only Super Admins and Operation Heads can change the SME"}
                      className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">— no SME —</option>
                      {smeList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select></div>
                </div>
                <div><label className="label-caps block mb-1">Freelancers</label>
                  <div className="border border-black/15 rounded-lg p-2 max-h-40 overflow-auto space-y-1">
                    {freelancers.map((f) => (
                      <label key={f.id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={(editing.assigned_freelancer_ids || []).includes(f.id)} onChange={(e) => {
                          const ids = e.target.checked ? [...(editing.assigned_freelancer_ids || []), f.id] : (editing.assigned_freelancer_ids || []).filter((x) => x !== f.id);
                          setEditing({ ...editing, assigned_freelancer_ids: ids });
                        }} />
                        {f.name} <span className="text-xs text-neutral-500">· {f.category}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <DialogFooter><button type="submit" data-testid="save-project-edit" className="btn-primary">Save</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={statusesOpen} onOpenChange={setStatusesOpen}>
        <DialogContent className="rounded-[1.25rem]" data-testid="manage-statuses-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Manage Project Statuses</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="new-status-input" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} placeholder="e.g. Awaiting Approval" className="rounded-lg border-black/15" />
              <button data-testid="add-status-btn" onClick={addStatus} className="btn-primary inline-flex items-center gap-1"><Plus size={14} /> Add</button>
            </div>
            <ul className="border border-black/10 rounded-lg divide-y divide-black/5 max-h-80 overflow-auto" data-testid="statuses-list">
              {statuses.map((s) => (
                <li key={s.id} className="flex justify-between items-center px-3 py-2 text-sm" data-testid={`status-row-${s.id}`}>
                  {renameFor === s.id ? (
                    <>
                      <Input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename(s)} className="h-8 rounded border-black/15 mr-2" data-testid={`rename-input-${s.id}`} />
                      <div className="flex gap-1">
                        <button onClick={() => saveRename(s)} className="text-royal-700 hover:bg-royal-700/10 rounded p-1" data-testid={`rename-save-${s.id}`}><Check size={14} /></button>
                        <button onClick={() => setRenameFor(null)} className="text-neutral-500 hover:bg-black/5 rounded p-1"><X size={14} /></button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span>{s.name}</span>
                      <div className="flex gap-1">
                        <button onClick={() => { setRenameFor(s.id); setRenameValue(s.name); }} className="text-royal-700 hover:bg-royal-700/10 rounded p-1" data-testid={`rename-status-${s.id}`}><Pencil size={14} /></button>
                        <button onClick={() => removeStatus(s)} className="text-[#B02A00] hover:bg-[#B02A00]/10 rounded p-1" data-testid={`delete-status-${s.id}`}><X size={14} /></button>
                      </div>
                    </>
                  )}
                </li>
              ))}
              {statuses.length === 0 && <li className="px-3 py-4 text-center text-neutral-500 text-sm">No statuses yet — add one above.</li>}
            </ul>
            <div className="text-xs text-neutral-500 font-mono-tight">Statuses are workspace-wide. Renaming updates every project using the old name; deleting is blocked while any project still uses it.</div>
          </div>
        </DialogContent>
      </Dialog>

      {isManager && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="sticky top-14 z-20 bg-royal-800 text-cream px-6 py-3 flex items-center gap-3 shadow-lg">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} selected</span>
          <select data-testid="bulk-status-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="text-xs bg-royal-900 border border-white/20 rounded px-2 py-1.5">
            <option value="">Change status to…</option>
            {statusOptions.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button data-testid="bulk-status-apply" disabled={!bulkStatus || bulkBusy} onClick={bulkChangeStatus} className="text-xs bg-gold text-ink rounded-full px-3 py-1.5 font-semibold disabled:opacity-50">Apply</button>
          <button data-testid="bulk-delete-btn" onClick={() => setBulkConfirmDelete(true)} className="text-xs bg-red-600 hover:bg-red-700 rounded-full px-3 py-1.5 font-semibold">Delete Selected</button>
          <button data-testid="bulk-clear-btn" onClick={clearSelection} className="text-xs underline underline-offset-4 ml-auto">Clear</button>
        </div>
      )}

      <Dialog open={bulkConfirmDelete} onOpenChange={(o) => !o && !bulkBusy && setBulkConfirmDelete(false)}>
        <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Delete {selected.size} project{selected.size > 1 ? "s" : ""}?</DialogTitle></DialogHeader>
          <div className="text-sm text-ink/70">This can't be undone. Work orders and files under these projects remain but become orphaned. Managers can only delete their own; skipped rows will be reported.</div>
          <div className="flex gap-3 pt-2">
            <button data-testid="bulk-delete-cancel" disabled={bulkBusy} onClick={() => setBulkConfirmDelete(false)} className="flex-1 rounded-full border border-black/15 py-2.5 text-sm font-semibold hover:bg-cream-2 disabled:opacity-50">Keep them</button>
            <button data-testid="bulk-delete-confirm" disabled={bulkBusy} onClick={bulkDelete} className="flex-1 rounded-full bg-red-600 text-white py-2.5 text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
              {bulkBusy ? "Deleting…" : "Yes, delete"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(o) => !o && !importBusy && setImportOpen(false)}>
        <DialogContent className="rounded-[1.25rem] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-projects-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2"><FileSpreadsheet size={20} /> Import projects via CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border border-black/10 rounded-2xl p-4 bg-cream-2/40">
              <div className="label-caps text-royal-800">Step 1 · Download template</div>
              <p className="text-sm text-ink/70 mt-1">Grab the CSV template with all supported columns and two example rows. Required: <b>Project Name</b>, <b>Client Name</b>. Optional: Manager Email, Status, Priority, Budget, Start Date, Due Date, Description.</p>
              <button data-testid="download-template-btn" onClick={downloadTemplate} className="mt-3 inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 text-sm hard-shadow-hover"><Download size={14} /> Download CSV template</button>
            </div>
            <div className="border border-black/10 rounded-2xl p-4">
              <div className="label-caps text-royal-800">Step 2 · Upload filled CSV</div>
              <div className="mt-2 flex items-center gap-3">
                <input data-testid="import-file-input" type="file" accept=".csv,text/csv" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} className="text-sm" />
                <button data-testid="import-upload-btn" disabled={!importFile || importBusy} onClick={submitImport} className="inline-flex items-center gap-2 bg-royal-800 text-cream rounded-full px-4 py-2 text-sm font-semibold hover:bg-royal-900 disabled:opacity-50">
                  <Upload size={14} /> {importBusy ? "Importing…" : "Upload & import"}
                </button>
              </div>
              {importFile && <div className="mt-2 text-xs text-ink/60 font-mono-tight">Selected: {importFile.name} · {Math.ceil(importFile.size / 1024)} KB</div>}
            </div>
            {importResult && (
              <div data-testid="import-result" className="border border-black/10 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-6">
                  <div><div className="label-caps text-[#1B4332]">Created</div><div className="font-display text-3xl text-[#1B4332]" data-testid="import-created-count">{importResult.created_count}</div></div>
                  <div><div className="label-caps text-red-600">Failed</div><div className="font-display text-3xl text-red-600" data-testid="import-failed-count">{importResult.failed_count}</div></div>
                </div>
                {importResult.created?.length > 0 && (
                  <details open>
                    <summary className="text-xs font-mono-tight cursor-pointer text-ink/70">Created projects ({importResult.created.length})</summary>
                    <ul className="mt-2 text-xs list-disc pl-5 text-ink/70 space-y-1">
                      {importResult.created.map((c) => (<li key={c.id} data-testid={`imported-${c.row}`}>Row {c.row}: {c.name}</li>))}
                    </ul>
                  </details>
                )}
                {importResult.errors?.length > 0 && (
                  <details open>
                    <summary className="text-xs font-mono-tight cursor-pointer text-red-600">Errors ({importResult.errors.length})</summary>
                    <ul className="mt-2 text-xs list-disc pl-5 text-red-700 space-y-1">
                      {importResult.errors.map((e, i) => (<li key={i} data-testid={`import-err-${e.row}`}>Row {e.row}: {e.reason}</li>))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
